/**
 * Utility functions for mapping between different billing period/cycle formats
 */

import { BillingCycleValue, ContractLengthValue, NoticePeriodValue, ContractBillingCycleValue, ContractLengthValueRestricted, isValidContractBillingCycle, isValidContractLengthRestricted } from '../constants/billing';

// Map from PriceList billing_period values to ContractLine billing_cycle values
export const BILLING_PERIOD_TO_CYCLE_MAP: Record<string, BillingCycleValue> = {
  'MONTHLY': '1_month',
  'QUARTERLY': '3_months',
  'SEMI_ANNUALLY': '6_months',
  'YEARLY': '12_months',
  'BI_ANNUALLY': '24_months',
  'WEEKLY': '1_month', // Default weekly to monthly for contract lines
};

// Map from ContractLine billing_cycle values to PriceList billing_period values
export const BILLING_CYCLE_TO_PERIOD_MAP: Record<string, string> = {
  '1_month': 'MONTHLY',
  '3_months': 'QUARTERLY',
  '6_months': 'SEMI_ANNUALLY',
  '12_months': 'YEARLY',
  '24_months': 'BI_ANNUALLY',
};

// Map from old format to new format (for backward compatibility)
export const OLD_TO_NEW_FORMAT_MAP: Record<string, BillingCycleValue | ContractLengthValue | NoticePeriodValue> = {
  '1 month': '1_month',
  '2 months': '2_months',
  '3 months': '3_months',
  '6 months': '6_months',
  '12 months': '12_months',
  '24 months': '24_months',
};

/**
 * Convert a PriceList billing_period to a ContractLine billing_cycle
 * @param billingPeriod - The billing period from a price list (e.g., 'MONTHLY')
 * @returns The corresponding billing cycle for contract lines (e.g., '1_month')
 */
export function convertBillingPeriodToCycle(billingPeriod: string): BillingCycleValue {
  return BILLING_PERIOD_TO_CYCLE_MAP[billingPeriod] || '1_month';
}

/**
 * Convert a ContractLine billing_cycle to a PriceList billing_period
 * @param billingCycle - The billing cycle from a contract line (e.g., '1_month')
 * @returns The corresponding billing period for price lists (e.g., 'MONTHLY')
 */
export function convertBillingCycleToPeriod(billingCycle: string): string {
  return BILLING_CYCLE_TO_PERIOD_MAP[billingCycle] || 'MONTHLY';
}

/**
 * Check if a value is a valid billing cycle for contract lines
 * @param value - The value to check
 * @returns True if the value is a valid billing cycle
 */
export function isValidBillingCycle(value: string): value is BillingCycleValue {
  return ['1_month', '3_months', '6_months', '12_months', '24_months'].includes(value);
}

/**
 * Check if a value is a valid billing period for price lists
 * @param value - The value to check
 * @returns True if the value is a valid billing period
 */
export function isValidBillingPeriod(value: string): boolean {
  return ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUALLY', 'YEARLY', 'BI_ANNUALLY', 'WEEKLY'].includes(value);
}

/**
 * Ensure a billing cycle value is valid, converting from billing period if necessary
 * @param value - The value to validate/convert
 * @returns A valid billing cycle value
 */
export function ensureValidBillingCycle(value: string): BillingCycleValue {
  // If it's already a valid billing cycle, return it
  if (isValidBillingCycle(value)) {
    return value;
  }
  
  // Check if it's in old format and convert
  if (value in OLD_TO_NEW_FORMAT_MAP) {
    const newValue = OLD_TO_NEW_FORMAT_MAP[value];
    if (isValidBillingCycle(newValue)) {
      return newValue as BillingCycleValue;
    }
  }
  
  // If it's a billing period, convert it
  if (isValidBillingPeriod(value)) {
    return convertBillingPeriodToCycle(value);
  }
  
  // Default to monthly
  return '1_month';
}

/**
 * Check if a value is a valid contract length
 * @param value - The value to check
 * @returns True if the value is a valid contract length
 */
export function isValidContractLength(value: string): value is ContractLengthValue {
  return ['1_month', '3_months', '6_months', '12_months', '24_months'].includes(value);
}

/**
 * Ensure a contract length value is valid
 * @param value - The value to validate
 * @returns A valid contract length value
 */
export function ensureValidContractLength(value: string): ContractLengthValue {
  if (isValidContractLength(value)) {
    return value;
  }
  
  // Check if it's in old format and convert
  if (value in OLD_TO_NEW_FORMAT_MAP) {
    const newValue = OLD_TO_NEW_FORMAT_MAP[value];
    if (isValidContractLength(newValue)) {
      return newValue as ContractLengthValue;
    }
  }
  
  // Default to 12 months
  return '12_months';
}

/**
 * Check if a value is a valid notice period
 * @param value - The value to check
 * @returns True if the value is a valid notice period
 */
export function isValidNoticePeriod(value: string): value is NoticePeriodValue {
  return ['1_month', '2_months', '3_months', '6_months', '12_months', '24_months'].includes(value);
}

/**
 * Ensure a notice period value is valid
 * @param value - The value to validate
 * @returns A valid notice period value
 */
export function ensureValidNoticePeriod(value: string): NoticePeriodValue {
  if (isValidNoticePeriod(value)) {
    return value;
  }
  
  // Check if it's in old format and convert
  if (value in OLD_TO_NEW_FORMAT_MAP) {
    const newValue = OLD_TO_NEW_FORMAT_MAP[value];
    if (isValidNoticePeriod(newValue)) {
      return newValue as NoticePeriodValue;
    }
  }
  
  // Map invalid values to valid ones
  if (value === '12_months' || value === '24_months') {
    return '6_months'; // Map longer periods to 6 months
  }
  
  // Default to 1 month
  return '1_month';
}

/**
 * Ensure a billing cycle value is valid for contracts (more restrictive)
 * @param value - The value to validate/convert
 * @returns A valid contract billing cycle value
 */
export function ensureValidContractBillingCycle(value: string): ContractBillingCycleValue {
  // If it's already a valid contract billing cycle, return it
  if (isValidContractBillingCycle(value)) {
    return value;
  }
  
  // Check if it's in old format and convert
  if (value in OLD_TO_NEW_FORMAT_MAP) {
    const newValue = OLD_TO_NEW_FORMAT_MAP[value];
    if (isValidContractBillingCycle(newValue)) {
      return newValue as ContractBillingCycleValue;
    }
  }
  
  // If it's a billing period, convert it and check if valid for contracts
  if (isValidBillingPeriod(value)) {
    const converted = convertBillingPeriodToCycle(value);
    if (isValidContractBillingCycle(converted)) {
      return converted as ContractBillingCycleValue;
    }
  }
  
  // If it's a valid general billing cycle but not valid for contracts, map to closest valid option
  if (isValidBillingCycle(value)) {
    if (value === '6_months' || value === '24_months') {
      return '12_months'; // Default semi-annual and bi-annual to yearly for contracts
    }
  }
  
  // Default to monthly
  return '1_month';
}

/**
 * Ensure a contract length value is valid for contracts (more restrictive)
 * @param value - The value to validate
 * @returns A valid contract length value for contracts
 */
export function ensureValidContractLengthRestricted(value: string): ContractLengthValueRestricted {
  // If it's already a valid contract length, return it
  if (isValidContractLengthRestricted(value)) {
    return value;
  }
  
  // Check if it's in old format and convert
  if (value in OLD_TO_NEW_FORMAT_MAP) {
    const newValue = OLD_TO_NEW_FORMAT_MAP[value];
    if (isValidContractLengthRestricted(newValue)) {
      return newValue as ContractLengthValueRestricted;
    }
  }
  
  // If it's a valid general contract length but not valid for contracts, map to closest valid option
  if (isValidContractLength(value)) {
    if (value === '6_months' || value === '24_months') {
      return '12_months'; // Default semi-annual and bi-annual to yearly for contracts
    }
  }
  
  // Default to 12 months
  return '12_months';
}